/**
 * Privacy policy content for the AppAI Browser Debug Chrome extension.
 * Source of truth for the public page at /privacy/browser-extension (also listed in the Chrome Web Store).
 */

export interface PolicySection {
  title: string;
  paragraphs?: string[];
  bullets?: string[];
}

export const BROWSER_EXTENSION_POLICY = {
  title: 'Privacy Policy — AppAI Browser Debug',
  subtitle: 'Chrome extension',
  effectiveDate: '2026-10-21',
  publisher: 'appaihelp',
  contactEmail: 'appaihelp.dev@gmail.com',
} as const;

export const BROWSER_EXTENSION_POLICY_SECTIONS: PolicySection[] = [
  {
    title: '1. What this extension is',
    paragraphs: [
      'AppAI Browser Debug is a companion to the appai command-line coding agent. It lets the agent that you run on your own computer open and inspect web pages in tabs the agent itself opens, so it can debug your web application.',
      'The extension does nothing on its own: it is idle unless the appai CLI on your computer is running and connected.',
    ],
  },
  {
    title: '2. How it works (data flow)',
    paragraphs: ['appai CLI (your computer) ⇄ local socket ⇄ native host ⇄ this extension ⇄ Chrome debugger.'],
    bullets: [
      'The extension communicates only with the appai CLI on your computer, through Chrome Native Messaging and a local socket. It contains no network code: it does not send data to any server by itself and does not load remote code.',
      'It only controls tabs in its own Chrome tab group named "AppAI", which the agent creates. Your other tabs are not visible to the agent.',
    ],
  },
  {
    title: '3. Data the extension can access',
    paragraphs: ['When the agent asks, the extension can read the following from a tab in the "AppAI" group:'],
    bullets: [
      "the page's accessibility tree (text and structure),",
      'console messages and network request metadata (URL, status, timing; response bodies only if your administrator enabled it),',
      'screenshots of the page,',
      'the result of JavaScript the agent evaluates, only if your administrator enabled it. Reading cookies and storage is refused.',
      'It can also perform actions in that tab (click, type, scroll, select, navigate) as requested by the agent.',
    ],
  },
  {
    title: '4. What happens to that data',
    bullets: [
      'The data above is returned to the appai CLI running on your computer.',
      'The CLI passes it to the AI coding agent as part of your conversation. It is processed by the AppAI service (appaihelp.com) and the AI model providers it uses, to answer your request. Screenshots may be analysed by an AI vision model for the same purpose.',
      'Before page content is passed on, the CLI and our service attempt to redact secret-looking values (such as API keys and tokens). Redaction is best-effort; do not open pages containing sensitive information you do not want an AI service to process.',
      'We do not sell this data, use it for advertising, or use it for any purpose unrelated to providing the debugging feature.',
      'Retention: conversation data (messages and tool results, including page content the agent read) is stored by the AppAI service for as long as your account and workspace exist, so you can resume and review your sessions. It is deleted when you delete the conversation or workspace, or your account. We currently apply no automatic expiry. Screenshots are analysed on the fly; only the resulting text description may be cached within the session (up to 20 entries).',
    ],
  },
  {
    title: '5. Data stored locally',
    bullets: [
      'The extension uses Chrome storage only for operational state (such as connection state and the agent\'s tab group).',
      'The appai CLI stores on your computer a list of web origins you chose to always allow (~/.appai/browser/allowed-origins.json).',
    ],
  },
  {
    title: '6. Your controls',
    bullets: [
      'The agent asks your approval before opening a new origin. You can allow it for the session, always allow it, or deny it.',
      'Organisation administrators can disable the feature, block origins, and keep JavaScript evaluation and response bodies off (the default).',
      'Chrome shows a "started debugging this browser" banner on every tab the agent controls.',
      'You can remove the extension at any time from chrome://extensions, and quit appai to stop all access.',
    ],
  },
  {
    title: '7. Security',
    paragraphs: [
      'Pages the agent reads are treated as untrusted content. Blocked origins are enforced before and after each command. Local communication uses a socket restricted to your user account.',
    ],
  },
  {
    title: '8. Children',
    paragraphs: ['This extension is not directed at children under 13.'],
  },
  {
    title: '9. Changes',
    paragraphs: ['We will post changes on this page and update the effective date.'],
  },
];
